import React, { useState } from "react";
import api from "../api/axios.tsx";
import Input from "./ui/input.tsx";
import Checkbox from "./ui/checkbox.tsx";
import Button from "./ui/button.tsx";
import { SimpleSelect } from "./ui/select-shadcn.tsx";

const AmortizationForm = () => {
  interface FormData {
      amount: number;
      interest: number;
      tenure_in_months: number;
      adjust: string;
      start_date: string;
      repayments: { month: number; amount: number; recurring: boolean }[];
      interest_revision: { month: number; interest: number }[];
      adjusted_emi_schedule: { month: number; emi: number }[];
  }
  
  const [formData, setFormData] = useState<FormData>({
      amount: 170000,
      interest: 8.6,
      tenure_in_months: 48,
      adjust: "tenure",
      start_date: new Date().toISOString().slice(0, 10),
      repayments: [],
      interest_revision: [],
      adjusted_emi_schedule: [],
  });

  const [repaymentInput, setRepaymentInput] = useState({ month: "", amount: "", recurring: false });
  const [emiChangeInput, setEmiChangeInput] = useState({ month: "", emi: "" });
  const [interestRevisionInput, setInterestRevisionInput] = useState({ month: "", interest: "" });

  interface Schedule {
    loan_amount: number;
    total_amount_paid: number;
    total_interest_paid: number;
    final_tenure_months: number;
    schedule: {
      month: number;
      date: string;
      emi: number;
      interest_component: number;
      principal_component: number;
      prepayment: number;
      remaining_principal: number;
    }[];
  }

  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddRepayment = () => {
    const { month, amount, recurring } = repaymentInput;
    if (!month || !amount) return;
    setFormData((prev) => ({
      ...prev,
      repayments: [...prev.repayments, { month: +month, amount: +amount, recurring }],
    }));
    setRepaymentInput({ month: "", amount: "", recurring: false });
  };

  const handleAddEMIChange = () => {
    const { month, emi } = emiChangeInput;
    if (!month || !emi) return;
    setFormData((prev) => ({
      ...prev,
      adjusted_emi_schedule: [...prev.adjusted_emi_schedule, { month: +month, emi: +emi }],
    }));
    setEmiChangeInput({ month: "", emi: "" });
  };

  const handleAddInterestRevision = () => {
    const { month, interest } = interestRevisionInput;
    if (!month || !interest) return;
    setFormData((prev) => ({
      ...prev,
      interest_revision: [...prev.interest_revision, { month: +month, interest: +interest }],
    }));
    setInterestRevisionInput({ month: "", interest: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/loans/amortization", formData);
      setSchedule(res.data);
    } catch (error) {
      console.error("Error fetching schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto bg-white rounded-lg shadow-xl">
      <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">
        📊 Loan Amortization Calculator
      </h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input type="number" name="amount" placeholder="Loan Amount" value={formData.amount} onChange={handleChange} />
          <Input type="number" name="interest" placeholder="Interest (%)" step="0.01" value={formData.interest} onChange={handleChange} />
          <Input type="number" name="tenure_in_months" placeholder="Tenure (months)" value={formData.tenure_in_months} onChange={handleChange} />
          <SimpleSelect
            value={formData.adjust}
            onValueChange={(v) => setFormData((prev) => ({ ...prev, adjust: v }))}
            options={[
              { label: "Adjust Tenure", value: "tenure" },
              { label: "Adjust EMI", value: "emi" },
            ]}
          />
          <Input type="date" name="start_date" value={formData.start_date} onChange={handleChange} />
        </div>

        <div>
          <h3 className="font-semibold mb-2">Add Repayment</h3>
          <div className="flex gap-2">
            <Input type="number" placeholder="Month" value={repaymentInput.month} onChange={(e) => setRepaymentInput({ ...repaymentInput, month: e.target.value })} />
            <Input type="number" placeholder="Amount" value={repaymentInput.amount} onChange={(e) => setRepaymentInput({ ...repaymentInput, amount: e.target.value })} />
            <label className="flex items-center space-x-1">
              <Checkbox checked={repaymentInput.recurring} onChange={(e) => setRepaymentInput({ ...repaymentInput, recurring: (e.target as HTMLInputElement).checked })} />
              <span>Recurring</span>
            </label>
            <Button type="button" onClick={handleAddRepayment}>Add</Button>
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-2">Add EMI Change</h3>
          <div className="flex gap-2">
            <Input type="number" placeholder="Month" value={emiChangeInput.month} onChange={(e) => setEmiChangeInput({ ...emiChangeInput, month: e.target.value })} />
            <Input type="number" placeholder="New EMI" value={emiChangeInput.emi} onChange={(e) => setEmiChangeInput({ ...emiChangeInput, emi: e.target.value })} />
            <Button type="button" onClick={handleAddEMIChange}>Add</Button>
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-2">Add Interest Revision</h3>
          <div className="flex gap-2">
            <Input type="number" placeholder="Month" value={interestRevisionInput.month} onChange={(e) => setInterestRevisionInput({ ...interestRevisionInput, month: e.target.value })} />
            <Input type="number" placeholder="New Interest Rate" value={interestRevisionInput.interest} onChange={(e) => setInterestRevisionInput({ ...interestRevisionInput, interest: e.target.value })} />
            <Button type="button" onClick={handleAddInterestRevision}>Add</Button>
          </div>
        </div>

        <Button type="submit" className="px-6">
          {loading ? "Calculating..." : "Generate Schedule"}
        </Button>
      </form>

      {schedule && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-2">Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg shadow">
            <div><p className="text-gray-500 text-sm">Loan Amount</p><p className="font-semibold">AED {schedule.loan_amount}</p></div>
            <div><p className="text-gray-500 text-sm">Total Paid</p><p className="font-semibold">AED {schedule.total_amount_paid}</p></div>
            <div><p className="text-gray-500 text-sm">Interest Paid</p><p className="font-semibold">AED {schedule.total_interest_paid}</p></div>
            <div><p className="text-gray-500 text-sm">Tenure</p><p className="font-semibold">{schedule.final_tenure_months} months</p></div>
          </div>

          <h2 className="text-xl font-semibold mt-6 mb-2">Amortization Schedule</h2>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="table-auto w-full text-sm text-left">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-2">Month</th>
                  <th className="p-2">Date</th>
                  <th className="p-2">EMI</th>
                  <th className="p-2">Interest</th>
                  <th className="p-2">Principal</th>
                  <th className="p-2">Prepayment</th>
                  <th className="p-2">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {schedule.schedule.map((entry) => (
                  <tr key={entry.month} className="hover:bg-gray-50">
                    <td className="p-2">{entry.month}</td>
                    <td className="p-2">{entry.date}</td>
                    <td className="p-2">{entry.emi.toFixed(2)}</td>
                    <td className="p-2">{entry.interest_component.toFixed(2)}</td>
                    <td className="p-2">{entry.principal_component.toFixed(2)}</td>
                    <td className="p-2">{entry.prepayment.toFixed(2)}</td>
                    <td className="p-2">{entry.remaining_principal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmortizationForm;
